import { Controller } from "@hotwired/stimulus";
import { csrfToken } from "../maps/picture_upload";
import type MapBoard from "./map_board_controller";

type Held = { id: string; name: string; url: string };
type Saved = Held & { count: number; limit: number };
type Extra = { replace_id?: string; keep_both?: boolean };
type View = "form" | "conflict" | "saved";

const fill = (text: string, name: string) => text.replace("%{name}", () => name);

// Connects to data-controller="map-save", on the master's board root beside map-board (docs/architecture/maps.md, "Meus Mapas").
// Salvar em Meus Mapas posts the open map to SavedMapsController#create and swaps the dialog between its three views: the form,
// the name the account already holds (Substituir or Guardar os dois) and the confirmation. The board itself is never touched.
export default class extends Controller<HTMLElement> {
	static targets = ["dialog", "view", "name", "description", "sharing", "limit", "countLine", "n", "alert", "submit", "held", "copy", "conflictButton", "open"];
	static values = { url: String, labels: Object };
	declare readonly dialogTarget: HTMLDialogElement;
	declare readonly viewTargets: HTMLElement[];
	declare readonly nameTarget: HTMLInputElement;
	declare readonly descriptionTarget: HTMLTextAreaElement;
	declare readonly sharingTarget: HTMLSelectElement;
	declare readonly limitTarget: HTMLElement;
	declare readonly countLineTarget: HTMLElement;
	declare readonly nTarget: HTMLElement;
	declare readonly hasCountLineTarget: boolean;
	declare readonly hasNTarget: boolean;
	declare readonly alertTargets: HTMLElement[];
	declare readonly submitTarget: HTMLButtonElement;
	declare readonly heldTarget: HTMLElement;
	declare readonly copyTarget: HTMLElement;
	declare readonly conflictButtonTargets: HTMLButtonElement[];
	declare readonly openTarget: HTMLAnchorElement;
	declare readonly urlValue: string;
	declare readonly labelsValue: { copy: string; network: string };
	held?: Held;
	sceneId = "";
	sending = false;

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard;
	}

	// Every opening starts from the form, filled with the map's name as it is now.
	open() {
		const { id, name } = this.board.state.view.peek().scene;
		this.sceneId = id;
		this.nameTarget.value = name;
		this.descriptionTarget.value = "";
		this.sharingTarget.selectedIndex = 0;
		this.show("form");
		this.dialogTarget.showModal();
		this.nameTarget.select();
	}

	close() {
		this.dialogTarget.close();
	}

	backdropClose(event: Event) {
		if (event.target === this.dialogTarget) this.close();
	}

	submit(event: Event) {
		event.preventDefault();
		return this.send({});
	}

	replace() {
		return this.held && this.send({ replace_id: this.held.id });
	}

	both() {
		return this.send({ keep_both: true });
	}

	back() {
		this.show("form");
		this.nameTarget.focus();
	}

	show(view: View) {
		for (const element of this.viewTargets) element.hidden = element.dataset.view !== view;
		this.alert("");
	}

	alert(text: string) {
		for (const element of this.alertTargets) {
			element.textContent = text;
			element.hidden = !text;
		}
	}

	busy(on: boolean) {
		this.sending = on;
		for (const button of [this.submitTarget, ...this.conflictButtonTargets]) button.disabled = on;
	}

	async send(extra: Extra) {
		if (this.sending) return;
		this.busy(true);
		this.alert("");
		try {
			const response = await fetch(this.urlValue, {
				method: "POST",
				credentials: "same-origin",
				headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() ?? "" },
				body: JSON.stringify({
					map_scene_id: this.sceneId,
					name: this.nameTarget.value,
					description: this.descriptionTarget.value,
					...(this.sharingTarget.disabled ? {} : { sharing: this.sharingTarget.value }),
					...extra,
				}),
			});
			const body = await response.json().catch(() => ({}));
			if (response.ok) this.saved(body);
			else if (response.status === 409 && body.conflict) this.conflict(body.conflict);
			else if (response.status === 403 && body.limit) this.limit();
			else if (response.status === 422 && body.errors?.length) this.alert(body.errors.join(" "));
			else this.alert(body.error ?? this.labelsValue.network);
		} catch {
			this.alert(this.labelsValue.network);
		} finally {
			this.busy(false);
		}
	}

	conflict(held: Held) {
		this.held = held;
		this.heldTarget.textContent = held.name;
		this.copyTarget.textContent = fill(this.labelsValue.copy, this.nameTarget.value.trim() || held.name);
		this.show("conflict");
		this.conflictButtonTargets.at(-1)?.focus();
	}

	// A new name over the plan's limit: Substituir, by typing a name already held, still works.
	limit() {
		this.show("form");
		this.showLimit(true);
		this.limitTarget.scrollIntoView({ block: "nearest" });
	}

	showLimit(full: boolean) {
		this.limitTarget.hidden = !full;
		if (this.hasCountLineTarget) this.countLineTarget.hidden = full;
	}

	saved({ url, count, limit }: Saved) {
		if (this.hasNTarget) this.nTarget.textContent = String(count + 1);
		this.showLimit(count >= limit);
		this.openTarget.href = url;
		this.show("saved");
		this.openTarget.focus();
	}
}
